import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowsOutCardinalIcon, CircleIcon, XIcon } from '@phosphor-icons/react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Drawer,
  Skeleton,
} from '@admin-panel/ui';
import { toast } from 'sonner';
import type { LookGroupDTOType } from '../../../../schemas/styling';
import { useLazyGetProductDepthByStyleNumbersQuery } from '../../../../store/stylesSearch/stylesSearchApi';
import {
  buildLookGroupsQueryArg,
  sortLookGroups,
  useGetLookGroupsListQuery,
  useMoveLookGroupMutation,
  useUpdateLookGroupMutation,
} from '../../../../store/stylist/stylistApi';
import AvailableGroupItem from './AvailableGroupItem';
import SortablePinnedItem from './SortablePinnedItem';
import { toLookGroupListItem } from './helpers';
import { EASE_OUT, MOTION_DURATION, getListContainerVariants, getListItemVariants } from './motion';
import type { LookGroupListItem, LookOrderDrawerProps } from './types';

const LookOrderDrawer = ({
  open,
  onClose,
  occasionId,
  occasionName,
  subOccasionId,
  subOccasionName,
}: LookOrderDrawerProps) => {
  const reduceMotion = useReducedMotion() || false;
  const closeRequestedFromInsideRef = useRef(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(open);
  const [pendingGroupId, setPendingGroupId] = useState<number | null>(null);
  const [isReorderingGroups, setIsReorderingGroups] = useState(false);
  const [localPinnedGroups, setLocalPinnedGroups] = useState<Array<LookGroupDTOType>>([]);

  useEffect(() => {
    if (open) {
      closeRequestedFromInsideRef.current = false;
      setIsDrawerOpen(true);
      return;
    }

    setIsDrawerOpen(false);
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  );

  const lookGroupQueryArg = useMemo(
    () => buildLookGroupsQueryArg(occasionId, subOccasionId),
    [occasionId, subOccasionId],
  );

  const {
    data: lookGroupData,
    isLoading: isLookGroupLoading,
    isError: isLookGroupError,
    error: lookGroupError,
  } = useGetLookGroupsListQuery(
    {
      ...lookGroupQueryArg,
      page_size: 1000,
    },
    {
      skip: !open || typeof occasionId !== 'number',
    },
  );

  const [updateLookGroup] = useUpdateLookGroupMutation();
  const [moveLookGroup] = useMoveLookGroupMutation();

  const fetchedLookGroups = useMemo(() => sortLookGroups(lookGroupData ?? []), [lookGroupData]);
  const pinnedGroups = useMemo(
    () => fetchedLookGroups.filter(group => group.is_pinned),
    [fetchedLookGroups],
  );

  useEffect(() => {
    setLocalPinnedGroups(pinnedGroups);
  }, [pinnedGroups]);

  const pinnedGroupItems = useMemo<Array<LookGroupListItem>>(
    () => localPinnedGroups.map(toLookGroupListItem),
    [localPinnedGroups],
  );

  const unpinnedGroupItems = useMemo<Array<LookGroupListItem>>(
    () => fetchedLookGroups.filter(group => !group.is_pinned).map(toLookGroupListItem),
    [fetchedLookGroups],
  );

  const pinnedGroupIds = useMemo(
    () => localPinnedGroups.map(group => group.id),
    [localPinnedGroups],
  );
  const primaryLookStyleNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          fetchedLookGroups
            .map(group => group.primary_look?.lk_style_number)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [fetchedLookGroups],
  );

  const [fetchProductDepth, productDepthQuery] = useLazyGetProductDepthByStyleNumbersQuery();

  useEffect(() => {
    if (!open || primaryLookStyleNumbers.length === 0) {
      return;
    }

    void fetchProductDepth(
      {
        styleNumbers: primaryLookStyleNumbers,
      },
      false,
    );
  }, [fetchProductDepth, open, primaryLookStyleNumbers]);

  const {
    data: productDepthData,
    isLoading: isProductDepthLoading,
    isError: isProductDepthError,
  } = productDepthQuery;

  const productDepthByGroupId = useMemo(
    () =>
      fetchedLookGroups.reduce<Record<number, { inventoryDepth: number; jitDepth: number }>>(
        (accumulator, group) => {
          const styleNumber = group.primary_look?.lk_style_number;
          if (!styleNumber) {
            return accumulator;
          }

          const summary = productDepthData?.depthByStyleNumber?.[styleNumber];
          if (!summary) {
            return accumulator;
          }

          accumulator[group.id] = {
            inventoryDepth: summary.inventoryDepth,
            jitDepth: summary.jitDepth,
          };

          return accumulator;
        },
        {},
      ),
    [fetchedLookGroups, productDepthData],
  );

  const listContainerVariants = useMemo(
    () => getListContainerVariants(reduceMotion),
    [reduceMotion],
  );
  const listItemVariants = useMemo(() => getListItemVariants(reduceMotion), [reduceMotion]);

  const isInitialLoading = isLookGroupLoading && !lookGroupData;

  const handlePinGroup = async (groupId: number) => {
    if (typeof occasionId !== 'number') {
      toast.error('Occasion context missing');
      return;
    }

    setPendingGroupId(groupId);

    try {
      const targetGroup = fetchedLookGroups.find(group => group.id === groupId);
      await updateLookGroup({
        id: groupId,
        data: {
          is_pinned: true,
          ...(typeof targetGroup?.primary_look_id === 'number'
            ? { primary_look_id: targetGroup.primary_look_id }
            : {}),
        },
      }).unwrap();

      toast.success('Look group pinned');
    } catch {
      toast.error('Could not pin look group');
    } finally {
      setPendingGroupId(null);
    }
  };

  const handleUnpinGroup = async (groupId: number) => {
    if (typeof occasionId !== 'number') {
      toast.error('Occasion context missing');
      return;
    }

    setPendingGroupId(groupId);

    try {
      await updateLookGroup({
        id: groupId,
        data: { is_pinned: false },
      }).unwrap();

      toast.success('Look group unpinned');
    } catch {
      toast.error('Could not unpin look group');
    } finally {
      setPendingGroupId(null);
    }
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (typeof occasionId !== 'number' || !over || active.id === over.id || isReorderingGroups) {
      return;
    }

    const activeId = Number(active.id);
    const overId = Number(over.id);
    const oldIndex = localPinnedGroups.findIndex(group => group.id === activeId);
    const newIndex = localPinnedGroups.findIndex(group => group.id === overId);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const previousPinnedGroups = localPinnedGroups;
    const reorderedPinnedGroups = [...localPinnedGroups];
    const [movedGroup] = reorderedPinnedGroups.splice(oldIndex, 1);
    reorderedPinnedGroups.splice(newIndex, 0, movedGroup);
    const beforeId = reorderedPinnedGroups[newIndex - 1]?.id ?? null;
    const afterId = reorderedPinnedGroups[newIndex + 1]?.id ?? null;

    setLocalPinnedGroups(reorderedPinnedGroups);
    setPendingGroupId(activeId);
    setIsReorderingGroups(true);

    try {
      await moveLookGroup({
        id: activeId,
        data: {
          before_id: beforeId,
          after_id: afterId,
        },
      }).unwrap();

      toast.success('Pinned look group order updated');
    } catch {
      setLocalPinnedGroups(previousPinnedGroups);
      toast.error('Could not update pinned look group order');
    } finally {
      setPendingGroupId(null);
      setIsReorderingGroups(false);
    }
  };

  return (
    <Drawer.Root
      open={isDrawerOpen}
      onOpenChange={nextOpen => {
        if (nextOpen) {
          closeRequestedFromInsideRef.current = false;
          setIsDrawerOpen(true);
          return;
        }

        closeRequestedFromInsideRef.current = true;
        setIsDrawerOpen(false);
      }}
      onAnimationEnd={nextOpen => {
        if (!nextOpen) {
          setPendingGroupId(null);
          setIsReorderingGroups(false);

          if (closeRequestedFromInsideRef.current) {
            closeRequestedFromInsideRef.current = false;
            onClose();
          }
        }
      }}
      direction="right"
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-1000 bg-black/35 backdrop-blur-sm transition-opacity ease-[cubic-bezier(0.215,0.61,0.355,1)] data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
        <Drawer.Content
          className="fixed inset-y-0 right-0 z-1000 flex h-full w-[min(96vw,920px)] translate-x-0 flex-col border-l border-border/70 bg-background outline-none transition-transform ease-[cubic-bezier(0.215,0.61,0.355,1)] data-[state=closed]:pointer-events-none data-[state=closed]:translate-x-full data-[state=open]:translate-x-0"
          style={{
            userSelect: 'text',
            WebkitUserSelect: 'text',
          }}
        >
          <header className="flex items-start justify-between border-b border-border px-4 py-3">
            <h2 className="flex items-center text-base justify-start gap-6 font-semibold text-foreground">
              {occasionName}
              <CircleIcon weight="fill" size={8} />
              {subOccasionName ?? null}
            </h2>
            <Drawer.Close asChild>
              <Button variant="subtle" size="small" icon={<XIcon size={16} />} aria-label="Close">
                Close
              </Button>
            </Drawer.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: MOTION_DURATION.standard, ease: EASE_OUT }
              }
              className="space-y-4"
            >
              {isLookGroupError ? (
                <Card className="border-rose-300 bg-rose-50">
                  <CardHeader>
                    <CardTitle className="text-sm text-rose-700">
                      Failed to load look groups
                    </CardTitle>
                    <CardDescription className="text-rose-600">
                      {String(lookGroupError)}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ) : isInitialLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : fetchedLookGroups.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center">
                  <p className="m-0 text-sm font-medium text-foreground">
                    No look groups found for this selection
                  </p>
                  <p className="mt-1 mb-0 text-xs text-muted-foreground">
                    Try a different occasion or sub-occasion scope.
                  </p>
                </div>
              ) : (
                <>
                  <section className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="m-0 text-sm font-semibold text-foreground">
                        Pinned Look Groups ({pinnedGroupItems.length})
                      </h3>
                      <Badge tone="neutral" icon={<ArrowsOutCardinalIcon size={12} />}>
                        Drag to reorder
                      </Badge>
                    </div>

                    {pinnedGroupItems.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                        No pinned groups yet.
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={pinnedGroupIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {pinnedGroupItems.map(item => (
                              <SortablePinnedItem
                                key={item.group.id}
                                item={item}
                                onUnpin={handleUnpinGroup}
                                isBusy={pendingGroupId === item.group.id || isReorderingGroups}
                                productDepth={{
                                  inventoryDepth:
                                    productDepthByGroupId[item.group.id]?.inventoryDepth ?? 0,
                                  jitDepth: productDepthByGroupId[item.group.id]?.jitDepth ?? 0,
                                  isLoading: isProductDepthLoading,
                                  isError: isProductDepthError,
                                }}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </section>

                  <section className="space-y-2">
                    <h3 className="m-0 text-sm font-semibold text-foreground">
                      Available Look Groups ({unpinnedGroupItems.length})
                    </h3>

                    {unpinnedGroupItems.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                        All look groups are pinned.
                      </div>
                    ) : (
                      <motion.div
                        variants={listContainerVariants}
                        initial={reduceMotion ? false : 'hidden'}
                        animate="visible"
                        className="space-y-2"
                      >
                        {unpinnedGroupItems.map(item => (
                          <AvailableGroupItem
                            key={item.group.id}
                            item={item}
                            onPin={handlePinGroup}
                            isBusy={pendingGroupId === item.group.id}
                            itemVariants={listItemVariants}
                            productDepth={{
                              inventoryDepth:
                                productDepthByGroupId[item.group.id]?.inventoryDepth ?? 0,
                              jitDepth: productDepthByGroupId[item.group.id]?.jitDepth ?? 0,
                              isLoading: isProductDepthLoading,
                              isError: isProductDepthError,
                            }}
                          />
                        ))}
                      </motion.div>
                    )}
                  </section>
                </>
              )}
            </motion.div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default LookOrderDrawer;
